import { KeyboardEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  fetchVisualizacaoEnderecos,
  fetchVisualizacaoEstoque,
  fetchVisualizacaoEtiquetas,
  visualizacaoEstoqueKeys,
} from '@/services/visualizacaoEstoqueService';
import type {
  IEstoqueGrupoItem,
  IVisualizacaoEstoqueSearch,
  TVisualizacaoDashboardFilter,
  TVisualizacaoFilterField,
} from '@/types/visualizacaoEstoque';
import './visualizacao-estoque.css';
import { Card } from '@/components/Card/Card';
import { TFilterField } from '@/types/relatorioEstoque';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';

export const Route = createFileRoute('/_authenticated/estoque/visualizacao-estoque/')({
  component: VisualizacaoEstoquePage,
});

const INITIAL_SEARCH: IVisualizacaoEstoqueSearch = {
  filterField: 'codigo_produto',
  filterValue: '',
};

const DIAS_VALIDADE_PROXIMA = 30;

function excelSerialParaData(excelDate: number | string): Date {
  const dataBase = new Date(1899, 11, 30);
  dataBase.setDate(dataBase.getDate() + parseInt(String(excelDate), 10));
  return dataBase;
}

function formatarData(excelDate: number | string | undefined): string {
  if (!excelDate) return '';
  return excelSerialParaData(excelDate).toLocaleDateString('pt-BR');
}

type TValidadeStatus = 'vencida' | 'proxima' | '';

function getValidadeStatus(excelDate: number | string | undefined): TValidadeStatus {
  if (!excelDate) return '';
  const dataValidade = excelSerialParaData(excelDate);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const diffDias = Math.ceil((dataValidade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDias < 0) return 'vencida';
  if (diffDias <= DIAS_VALIDADE_PROXIMA) return 'proxima';
  return '';
}

const columns: string[] = ['Código Produto', 'Descrição', 'Lote', 'Validade', 'Endereço', 'Peso Líquido (KG)', 'Caixas'];

function VisualizacaoEstoquePage() {
  const [filterField, setFilterField] = useState<TVisualizacaoFilterField>(
    INITIAL_SEARCH.filterField,
  );
  const [filterValue, setFilterValue] = useState(INITIAL_SEARCH.filterValue);
  const [activeSearch, setActiveSearch] = useState<IVisualizacaoEstoqueSearch>(INITIAL_SEARCH);
  const [selectedGroup, setSelectedGroup] = useState<IEstoqueGrupoItem | null>(null);
  const [showValidadeModal, setShowValidadeModal] = useState(false);
  const [fetchEnderecos, setFetchEnderecos] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const {
    data: estoqueData,
    isError: isEstoqueError,
    error: estoqueError,
  } = useQuery({
    queryKey: visualizacaoEstoqueKeys.estoque(activeSearch),
    queryFn: () => fetchVisualizacaoEstoque(activeSearch),
  });

  const {
    data: etiquetasData,
    isError: isEtiquetasError,
    error: etiquetasError,
  } = useQuery({
    queryKey: visualizacaoEstoqueKeys.etiquetas(selectedGroup),
    queryFn: () => fetchVisualizacaoEtiquetas(selectedGroup!),
    enabled: selectedGroup !== null,
  });

  const {
    data: enderecosData,
    isSuccess: enderecosSuccess,
    isError: isEnderecosError,
    error: enderecosError,
  } = useQuery({
    queryKey: visualizacaoEstoqueKeys.enderecos,
    queryFn: fetchVisualizacaoEnderecos,
    enabled: fetchEnderecos,
  });

  const estoque = estoqueData?.estoque ?? [];
  const totalCodigosUnicos = estoqueData?.total_codigos_unicos ?? 0;
  const totalEnderecos = estoqueData?.total_enderecos ?? 0;
  const totalPeso = estoqueData?.peso_total_geral ?? 0;
  const caixasValidadeProxima = estoqueData?.caixas_validade_proxima ?? [];
  const totalCaixas = estoqueData?.total_caixas ?? 0;
  const etiquetas = etiquetasData?.etiquetas ?? [];
  const enderecos = enderecosData?.enderecos ?? [];

  const pesoValidadeProxima = caixasValidadeProxima.reduce((acc, item) => acc + item[5], 0);

  const showEnderecoModal = fetchEnderecos && enderecosSuccess;

  const handleFilter = () => {
    setActionMessage('');
    setActiveSearch({
      filterField,
      filterValue,
    });
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setFilterField('codigo_produto');
    setActionMessage('');
    setActiveSearch(INITIAL_SEARCH);
  };

  const handleKpiKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    filterType: TVisualizacaoDashboardFilter,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDashboardClick(filterType);
    }
  };

  const handleDashboardClick = (filterType: TVisualizacaoDashboardFilter) => {
    setActionMessage('');
    if (filterType === 'validade_proxima') {
      setShowValidadeModal(true);
    } else if (filterType === 'enderecos_utilizados') {
      setFetchEnderecos(true);
    } else {
      setActiveSearch((prev) => ({
        ...prev,
        dashboardFilter: filterType,
      }));
    }
  };

  const handleRowClick = (grupo: IEstoqueGrupoItem) => {
    setSelectedGroup(grupo);
    setActionMessage('');
  };

  const handleCloseEnderecoModal = () => {
    setFetchEnderecos(false);
  };

  const queryErrorMessage = isEstoqueError
    ? estoqueError instanceof Error
      ? estoqueError.message
      : 'Erro ao carregar o estoque'
    : isEtiquetasError
      ? etiquetasError instanceof Error
        ? etiquetasError.message
        : 'Erro ao buscar etiquetas'
      : isEnderecosError
        ? enderecosError instanceof Error
          ? enderecosError.message
          : 'Erro ao carregar endereços'
        : '';

  const apiErrorMessage =
    estoqueData?.error || etiquetasData?.error || enderecosData?.error || '';

  const message = actionMessage || queryErrorMessage || apiErrorMessage;

  return (
    <div className="visualizacao-estoque-container">
      <div className="visualizacao-estoque-header">
        <p className="filter-info">
          Exibindo apenas itens do estoque Fresco Indústria disponíveis.
        </p>
        <Card className="filter">
          <Card.Content className="filter-card__content">
            <div className="filter-grid">
              <div className="input-content filter-grid__field">
                <label htmlFor="filterField">Campo de Filtro</label>
                <select
                  id="filterField"
                  value={filterField}
                  onChange={(e) => setFilterField(e.target.value as TFilterField)}
                >
                  <option value="codigo_produto">Código Produto</option>
                  <option value="lote">Lote</option>
                  <option value="descricao">Descrição</option>
                  <option value="endereco">Endereço</option>
                </select>
              </div>

              <div className="input-content filter-grid__value">
                <label htmlFor="filterValue">Valor para busca</label>
                <input
                  id="filterValue"
                  type="text"
                  placeholder="Digite o termo para pesquisar..."
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                />
              </div>

              <button
                type="button"
                onClick={handleFilter}
                className="filter-button"
              >
                Filtrar
              </button>

              <button
                type="button"
                onClick={handleClearFilter}
                className="clear-filter-button"
              >
                Limpar Filtro
              </button>
            </div>
          </Card.Content>
        </Card>
      </div>

      <div className="visualizacao-estoque-content">
        <div className="cards">
          <Card
            role="button"
            tabIndex={0}
            aria-label="Filtrar tabela por códigos únicos"
            onClick={() => handleDashboardClick('codigos_unicos')}
            onKeyDown={(e) => handleKpiKeyDown(e, 'codigos_unicos')}
          >
            <Card.Header>
              <h3>Códigos Únicos</h3>
            </Card.Header>
            <Card.Content>
              <p>{totalCodigosUnicos}</p>
            </Card.Content>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label="Ver endereços utilizados"
            onClick={() => handleDashboardClick('enderecos_utilizados')}
            onKeyDown={(e) => handleKpiKeyDown(e, 'enderecos_utilizados')}
          >
            <Card.Header>
              <h3>Endereços Utilizados</h3>
            </Card.Header>
            <Card.Content>
              <p>{totalEnderecos}</p>
            </Card.Content>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label="Ver produtos com validade próxima do vencimento"
            onClick={() => handleDashboardClick('validade_proxima')}
            onKeyDown={(e) => handleKpiKeyDown(e, 'validade_proxima')}
          >
            <Card.Header>
              <h3>Próximos Vencimento</h3>
            </Card.Header>
            <Card.Content>
              <p>
                {caixasValidadeProxima.reduce((acc, row) => acc + (row[4] ?? 0), 0)} caixas /{' '}
                {Number(pesoValidadeProxima.toFixed(2)).toLocaleString('pt-BR')} Kg
              </p>
            </Card.Content>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label="Filtrar tabela por total de caixas"
            onClick={() => handleDashboardClick('total_caixas')}
            onKeyDown={(e) => handleKpiKeyDown(e, 'total_caixas')}
          >
            <Card.Header>
              <h3>Total de Caixas</h3>
            </Card.Header>
            <Card.Content>
              <p>{Number(totalCaixas).toLocaleString('pt-BR')}</p>
            </Card.Content>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            aria-label="Filtrar tabela por peso total"
            onClick={() => handleDashboardClick('peso_total')}
            onKeyDown={(e) => handleKpiKeyDown(e, 'peso_total')}
          >
            <Card.Header>
              <h3>Peso Total (Kg)</h3>
            </Card.Header>
            <Card.Content>
              <p>{Number(totalPeso.toFixed(2)).toLocaleString('pt-BR')} Kg</p>
            </Card.Content>
          </Card>
        </div>
        <p className="cards-hint">Clique em um indicador para filtrar a tabela abaixo.</p>

        <div className="visualizacao-estoque-table-container">
          <Table className="estoque-table">
            <caption className="sr-only">Estoque Fresco Indústria</caption>
            <Table.Header>
              <Table.Row>
                {columns.map((item, index) => (
                  <Table.Head
                    key={index}
                    scope="col"
                  >
                    {item}
                  </Table.Head>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {estoque.map((item, index) => {
                const validadeStatus = getValidadeStatus(item.validade);
                return (
                  <Table.Row
                    key={index}
                    onClick={() => handleRowClick(item)}
                    className={selectedGroup === item ? 'selected-row' : ''}
                  >
                    <Table.Cell>{item.codigo_produto}</Table.Cell>
                    <Table.Cell>{item.descricao}</Table.Cell>
                    <Table.Cell>{item.lote}</Table.Cell>
                    <Table.Cell
                      className={validadeStatus ? `validade-status--${validadeStatus}` : undefined}
                    >
                      {formatarData(item.validade)}
                    </Table.Cell>
                    <Table.Cell>{item.endereco}</Table.Cell>
                    <Table.Cell>
                      {item.peso_total ? item.peso_total.toFixed(2) : '0.00'} Kg
                    </Table.Cell>
                    <Table.Cell>{item.caixas}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Content>
          </Table>
        </div>
      </div>

      {showValidadeModal && (
        <Modal
          className="relatorio-estoque-modal"
          onClose={() => setShowValidadeModal(false)}
        >
          <Modal.Header title="Produtos Próximos do Vencimento" />
          <Modal.Content>
            <Table>
              <caption className="sr-only">Produtos próximos do vencimento</caption>
              <Table.Header>
                <Table.Row>
                  <Table.Head scope="col">Código Produto</Table.Head>
                  <Table.Head scope="col">Descrição</Table.Head>
                  <Table.Head scope="col">Endereço</Table.Head>
                  <Table.Head scope="col">Validade</Table.Head>
                  <Table.Head scope="col">Quantidade de Caixas</Table.Head>
                  <Table.Head scope="col">Peso Total (Kg)</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Content>
                {caixasValidadeProxima.map((item, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{item[0]}</Table.Cell>
                    <Table.Cell>{item[1]}</Table.Cell>
                    <Table.Cell>{item[2] || '-'}</Table.Cell>
                    <Table.Cell
                      className={
                        getValidadeStatus(item[3]) ? `validade-status--${getValidadeStatus(item[3])}` : undefined
                      }
                    >
                      {formatarData(item[3])}
                    </Table.Cell>
                    <Table.Cell>{item[4]}</Table.Cell>
                    <Table.Cell>{item[5].toFixed(2)} Kg</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Content>
            </Table>
          </Modal.Content>
        </Modal>
      )}

      {showEnderecoModal && (
        <Modal
          className="relatorio-estoque-modal"
          onClose={() => handleCloseEnderecoModal()}
        >
          <Modal.Header title="Endereços Utilizados" />
          <Modal.Content>
            <div className="enderecos-container">
              {enderecos.map((endereco, index) => (
                <Card
                  key={index}
                  className="endereco-bloco"
                >
                  <Card.Header>
                    <h3>{endereco.endereco}</h3>
                  </Card.Header>
                  <Card.Content>
                    <p>
                      <strong>Caixas:</strong> {endereco.caixas}
                    </p>
                    <p>
                      <strong>Peso:</strong> {Number(endereco.KG).toLocaleString('pt-BR')} Kg
                    </p>
                  </Card.Content>
                </Card>
              ))}
            </div>
          </Modal.Content>
        </Modal>
      )}

      {etiquetas.length > 0 && (
        <div className="etiquetas-section">
          <h3>Etiquetas do Grupo Selecionado</h3>
          <Table className="etiquetas-table">
            <caption className="sr-only">Etiquetas do grupo selecionado</caption>
            <Table.Header>
              <Table.Row>
                <Table.Head scope="col">Código de Barras</Table.Head>
                <Table.Head scope="col">Código Produto</Table.Head>
                <Table.Head scope="col">Lote</Table.Head>
                <Table.Head scope="col">Endereço</Table.Head>
                <Table.Head scope="col">Peso Líquido (Kg)</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {etiquetas.map((etiqueta, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{etiqueta.codigo_barras}</Table.Cell>
                  <Table.Cell>{etiqueta.codigo_produto}</Table.Cell>
                  <Table.Cell>{etiqueta.lote}</Table.Cell>
                  <Table.Cell>{etiqueta.endereco}</Table.Cell>
                  <Table.Cell>
                    {etiqueta.peso_liquido ? etiqueta.peso_liquido.toFixed(2) : '0.00'} Kg
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Content>
          </Table>
        </div>
      )}
    </div>
  );
}
